import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPrintletEvents } from "../../api/PrintletAPIs";
import {
  Activity,
  ChevronRight,
  ChevronLeft,
  Inbox,
  Radio,
  Clock,
  Server,
} from "lucide-react";

const EVENT_COLORS = {
  order:       "bg-indigo-50 text-indigo-700 border-indigo-200",
  status:      "bg-sky-50 text-sky-700 border-sky-200",
  shipment:    "bg-violet-50 text-violet-700 border-violet-200",
  payment:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  error:       "bg-rose-50 text-rose-700 border-rose-200",
};

function getEventColor(eventType = "") {
  const t = eventType.toLowerCase();
  if (t.includes("ship")) return EVENT_COLORS.shipment;
  if (t.includes("pay")) return EVENT_COLORS.payment;
  if (t.includes("error") || t.includes("fail")) return EVENT_COLORS.error;
  if (t.includes("status")) return EVENT_COLORS.status;
  return EVENT_COLORS.order;
}

function EventRow({ ev }) {
  const color = getEventColor(ev.eventType);
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/60 px-2 rounded-lg transition">
      <div className={`shrink-0 mt-0.5 p-2 rounded-xl border ${color}`}>
        <Activity size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-bold text-slate-800 text-sm">{ev.eventType}</span>
          {ev.status && (
            <span className="px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
              {ev.status}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          {ev.eventId && (
            <span className="font-mono truncate max-w-[180px]" title={ev.eventId}>
              {ev.eventId}
            </span>
          )}
          {ev.source && (
            <span className="inline-flex items-center gap-1">
              <Server size={11} className="text-slate-400" />
              {ev.source}
            </span>
          )}
          {(ev.occurredAtUtc || ev.receivedAtUtc) && (
            <span className="inline-flex items-center gap-1" dir="ltr">
              <Clock size={11} className="text-slate-400" />
              {ev.occurredAtUtc || ev.receivedAtUtc}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PrintletEventsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data, isLoading } = useQuery({
    queryKey: ["printletEvents", page],
    queryFn: () => fetchPrintletEvents(page),
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Radio size={22} className="text-indigo-600" />
          أحداث Printlet
        </h2>
        <p className="text-sm text-slate-500 mt-1">سجل Webhook events الواردة من Printlet</p>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-slate-100 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-1/3" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-4 rounded-full bg-slate-100 mb-3">
              <Inbox size={32} className="text-slate-400" />
            </div>
            <h3 className="font-bold text-slate-700 mb-1">لا توجد أحداث</h3>
            <p className="text-sm text-slate-500 max-w-xs">
              لم يتم استلام أي Webhook events من Printlet بعد.
            </p>
          </div>
        ) : (
          <div className="p-2">
            {items.map((ev) => (
              <EventRow key={ev.eventId || ev.id} ev={ev} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && items.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-t border-slate-200">
            <div className="text-sm text-slate-500">
              صفحة <b className="text-slate-700">{page}</b> من{" "}
              <b className="text-slate-700">{totalPages}</b>
            </div>
            <div className="flex items-center gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={15} /> السابق
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                التالي <ChevronLeft size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}